// =============================================================================
// Pattern classifier — full 3-layer pipeline.
//
// Layer 1: threshold evaluation per life stage (thresholds.ts)
// Layer 2: pattern trigger evaluation (patterns.ts)
// Layer 3: arbitration — priority-ordered suppression rules (arbitrate.ts)
//
// Returns the full Classification (audit trail, citations, confidence, severity).
// =============================================================================

import { arbitrate, type WinnerPatternId } from './arbitrate.js';
import { PATTERN_METADATA } from './pattern-metadata.js';
import { evaluatePatterns } from './patterns.js';
import { evaluateThresholds } from './thresholds.js';
import { recommendNextTest } from './next-test.js';
import type { Classification, CompletenessTier, LabInputs } from './types.js';

/** Full 3-layer classification. */
export function classify(inputs: LabInputs): Classification {
  // Guard: at least one lab value required.
  if (!hasAnyLabValue(inputs)) {
    const metadata = PATTERN_METADATA.insufficient_data;
    return {
      patternId: 'insufficient_data',
      laypersonName: metadata.laypersonName,
      technicalName: 'No lab values provided',
      strength: metadata.strength,
      confidenceTier: 'low',
      severity: 'routine',
      completeness: detectCompleteness(inputs),
      arbitrationTrace: ['no_labs_provided'],
      primaryCitations: [],
      supportingCitations: [],
      manifestRefs: [],
      inflammationCaveatApplied: isInflammationFlagged(inputs),
      supplementationCaveatApplied: isIronSupplementationFlagged(inputs),
      mostInformativeNextTest: 'ferritin',
    };
  }

  // Layer 1: evaluate thresholds per life stage.
  const thresholdEval = evaluateThresholds(inputs);

  // Layer 2: which substantive patterns fire?
  const firedPatterns = evaluatePatterns(thresholdEval, inputs);

  // Layer 3: arbitration → single winner + severity + confidence + caveats.
  const result = arbitrate(firedPatterns, thresholdEval, inputs);

  // Look up pattern-level user-facing strings + citations.
  const metadata = PATTERN_METADATA[result.winner];

  return {
    patternId: result.winner,
    laypersonName: metadata.laypersonName,
    technicalName: metadata.technicalName,
    strength: metadata.strength,
    confidenceTier: result.confidenceTier,
    severity: result.severity,
    completeness: result.completeness,
    arbitrationTrace: result.arbitrationTrace,
    primaryCitations: metadata.primaryCitations,
    supportingCitations: metadata.supportingCitations,
    manifestRefs: metadata.manifestRefs,
    inflammationCaveatApplied: result.inflammationCaveat,
    supplementationCaveatApplied: result.supplementationCaveat,
    secondaryCandidates: result.secondaryCandidates,
    mostInformativeNextTest: computeMostInformativeNextTest(result.winner, inputs),
  };
}

/**
 * Recommend the single lab test that would most improve a low-confidence
 * classification.
 *
 * For `insufficient_data`, uses the next-test forward-look (next-test.ts)
 * which sweeps plausible values for each missing core marker and returns
 * the one most likely to resolve to a substantive pattern given the user's
 * existing inputs. Falls back to ferritin if the forward-look declines
 * to recommend (every missing marker yields meta patterns across the
 * sweep — genuinely unresolvable through any single test).
 *
 * For other low-confidence cases, uses static pattern-specific routing:
 *   - mixed_picture without TSAT → TSAT (resolves the iron-utilization gap)
 *   - mixed_picture with recent illness + no CRP → CRP (rules in/out
 *     inflammation distortion)
 *   - anemia_with_replete_ferritin without MCV → MCV (literature-standard
 *     anemia-subtype discriminator)
 */
function computeMostInformativeNextTest(
  winner: WinnerPatternId,
  inputs: LabInputs,
): string | undefined {
  if (winner === 'insufficient_data') {
    const recommendation = recommendNextTest(inputs, classify);
    if (recommendation) return recommendation.displayName;
    // Fallback: every sweep yielded a meta pattern (very unusual — most
    // commonly when the user has no labs at all). Default to ferritin
    // because the literature anchors it as the most informative single
    // marker.
    if (inputs.ferritin === undefined) return 'ferritin';
    return undefined;
  }
  if (winner === 'mixed_picture_multiple_patterns' && inputs.tsat === undefined) return 'TSAT';
  if (
    winner === 'mixed_picture_multiple_patterns' &&
    inputs.crp === undefined &&
    inputs.recentIllness2Weeks
  ) {
    return 'CRP';
  }
  // Anemia with replete ferritin needs MCV-based morphology differentiation
  // (the literature-standard discriminator for anemia subtype).
  if (winner === 'anemia_with_replete_ferritin' && inputs.mcv === undefined) return 'MCV';
  return undefined;
}

/**
 * Detect which completeness tier the user's inputs qualify for.
 * Governs which outputs are available per decision-logic.yaml.
 */
export function detectCompleteness(inputs: LabInputs): CompletenessTier {
  const hasCBCMinimum = inputs.hemoglobin !== undefined && inputs.mcv !== undefined;
  const hasFerritin = inputs.ferritin !== undefined;
  const hasTSAT = inputs.tsat !== undefined;

  if (hasCBCMinimum && hasFerritin && hasTSAT) return 'tier_3_full_panel';
  if (hasCBCMinimum && hasFerritin) return 'tier_2_cbc_plus_ferritin';
  return 'tier_1_cbc_only';
}

/**
 * True when inflammation should flag ferritin interpretation
 * (CRP ≥ 5 mg/L, or user-reported recent illness).
 */
export function isInflammationFlagged(inputs: LabInputs): boolean {
  if (inputs.crp !== undefined && inputs.crp >= 5) return true;
  if (inputs.recentIllness2Weeks === true) return true;
  return false;
}

/**
 * True when recent / current iron supplementation should flag ferritin
 * interpretation. Oral iron meaningfully raises ferritin within 2–4 weeks
 * of dosing and the elevation persists after stopping. The engine treats
 * this as a ferritin-interpretation caveat — same mechanism as the
 * inflammation caveat, different cause. The 2-week "stopped_recent"
 * window matches the recent-illness-2-weeks window for interpretive
 * consistency across the wizard.
 */
export function isIronSupplementationFlagged(inputs: LabInputs): boolean {
  return (
    inputs.ironSupplementation === 'currently' ||
    inputs.ironSupplementation === 'stopped_recent'
  );
}

/**
 * True when any ferritin-interpretation caveat applies — inflammation OR
 * recent supplementation. Used by Layer 3 to downgrade confidence on
 * ferritin-dependent patterns.
 */
export function isFerritinCaveatFlagged(inputs: LabInputs): boolean {
  return isInflammationFlagged(inputs) || isIronSupplementationFlagged(inputs);
}

/** True when at least one lab value was provided. */
export function hasAnyLabValue(inputs: LabInputs): boolean {
  return (
    inputs.ferritin !== undefined ||
    inputs.hemoglobin !== undefined ||
    inputs.mcv !== undefined ||
    inputs.mch !== undefined ||
    inputs.rdw !== undefined ||
    inputs.tsat !== undefined ||
    inputs.serumIron !== undefined ||
    inputs.tibc !== undefined ||
    inputs.crp !== undefined ||
    inputs.stfr !== undefined ||
    inputs.retHe !== undefined
  );
}
